import React, { useEffect, useRef, useState } from "react";

import {
  forgotPassword,
  resendOTP,
  verifyPasswordResetOTP,
  resetPassword,
} from "../../api/auth";
import { Eye } from "lucide-react";
import { AiFillEyeInvisible } from "react-icons/ai";
import { useTranslation } from "../../hooks/useTranslation";
const ForgotPassword = () => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0); // 0 = Email, 1 = OTP, 2 = Password
  const [email, setEmail] = useState("");
  const [otpDigits, setOtpDigits] = useState(Array(6).fill(""));
  const [timer, setTimer] = useState(60);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const otpRefs = useRef([]);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    let interval;
    if (step === 1 && timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [step, timer]);

  const handleSendOtp = async () => {
    if (!email) {
      setError(t("forgotPassword").errors.emailRequired);
      return;
    }

    setLoading(true);
    try {
      const response = await forgotPassword({ email });
      if (response.success) {
        setStep(1);
        setTimer(60);
        setError("");
      } else {
        setError(
          response.data.message || t("forgotPassword").errors.sendFailed
        );
      }
    } catch (err) {
      setError(t("forgotPassword").errors.generalError);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otp = otpDigits.join("");
    if (otp.length < 6) {
      setError(t("forgotPassword").errors.otpRequired);
      return;
    }

    setLoading(true);
    try {
      const response = await verifyPasswordResetOTP({ email, otp });

      if (response.success) {
        setToken(response.data.resetToken);
        setStep(2);
        setError("");
      } else {
        const errorMessage = response.message || "";
        const isOtpError = /Invalid\s+OTP/i.test(errorMessage);
        setError(
          isOtpError ? t("forgotPassword").errors.otpInvalid : errorMessage || t("forgotPassword").errors.otpInvalid
        );
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || "";
      const isOtpError = /Invalid\s+OTP/i.test(errorMessage);
      setError(
        isOtpError ? t("forgotPassword").errors.otpInvalid : errorMessage || t("forgotPassword").errors.generalError
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (timer > 0) return;

    setLoading(true);
    try {
      const response = await resendOTP({ email });
      if (response.success) {
        setTimer(60);
        alert(t("forgotPassword").successReset);
        setError("");
      } else {
        setError(
          response.data.message || t("forgotPassword").errors.resendFailed
        );
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || t("forgotPassword").errors.generalError
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      setError(t("forgotPassword").errors.fieldsRequired);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("forgotPassword").errors.passwordMismatch);
      return;
    }

    setLoading(true);
    try {
      const response = await resetPassword({
        email,
        token,
        newPassword,
        confirmPassword,
      });
      if (response.success) {
        alert(t("forgotPassword").successReset);
        window.location.href = "/login";
      } else {
        setError(
          response.data.message || t("forgotPassword").errors.resetFailed
        );
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || t("forgotPassword").errors.generalError
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (val, idx) => {
    if (!/^[0-9]?$/.test(val)) return;

    const updated = [...otpDigits];
    updated[idx] = val;
    setOtpDigits(updated);

    if (val && idx < 5) {
      otpRefs.current[idx + 1]?.focus();
    }
  };

  const handleOtpBackspace = (e, idx) => {
    if (e.key === "Backspace" && !otpDigits[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleBack = () => {
    if (step === 0) {
      window.location.href = "/login";
    } else {
      setStep(step - 1);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="max-w-md mx-auto  p-6 border rounded-lg shadow-sm bg-white">
        <h2 className="text-2xl font-bold mb-2">{t("forgotPassword").title}</h2>
        <p className="text-gray-500 mb-4">
          {step === 0 && t("forgotPassword").subtitleEmail}
          {step === 1 && t("forgotPassword").subtitleOtp}
          {step === 2 && t("forgotPassword").subtitleNewPassword}
        </p>

        {step !== 1 && error && <div className="text-red-500 text-sm mb-4">{error}</div>}

        {step === 0 && (
          <>
            <input
              type="email"
              placeholder={t("forgotPassword").emailPlaceholder}
              className="w-full p-3 border rounded mb-4"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              onClick={handleSendOtp}
              className="w-full bg-green-600 text-white py-3 rounded hover:bg-green-700 transition"
              disabled={loading}
            >
              {loading
                ? t("forgotPassword").sending
                : t("forgotPassword").sendOtp}
            </button>
          </>
        )}

        {step === 1 && (
          <>
            <div className="flex justify-between mb-4">
              {otpDigits.map((digit, idx) => (
                <input
                  key={idx}
                  type="text"
                  maxLength={1}
                  value={digit}
                  className="w-12 h-12 border text-center rounded"
                  onChange={(e) => handleOtpChange(e.target.value, idx)}
                  onKeyDown={(e) => handleOtpBackspace(e, idx)}
                  ref={(el) => (otpRefs.current[idx] = el)}
                />
              ))}
            </div>
            {error && <div className="text-red-500 text-sm mb-4">{error}</div>}
            <button
              onClick={handleVerifyOtp}
              className="w-full bg-green-600 text-white py-3 rounded hover:bg-green-700 transition"
              disabled={loading}
            >
              {loading
                ? t("forgotPassword").verifying
                : t("forgotPassword").verifyOtp}
            </button>
            <div className="text-sm text-center mt-4 text-gray-600">
              {timer > 0 ? (
                <>
                  {t("forgotPassword").resendIn} {timer} {t("forgotPassword").seconds}
                </>
              ) : (
                <button
                  onClick={handleResendOtp}
                  className="text-green-600 font-medium"
                >
                  {t("forgotPassword").resend}
                </button>
              )}
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <div className="relative w-full mb-4">
              <input
                type={showNewPassword ? "text" : "password"}
                placeholder={t("forgotPassword").newPassword}
                className="w-full p-3 border rounded pr-10"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                aria-label={showNewPassword ? "Hide password" : "Show password"}
              >
                {showNewPassword ? (
                  <AiFillEyeInvisible size={20} />
                ) : (
                  <Eye size={20} />
                )}
              </button>
            </div>

            <div className="relative w-full mb-4">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                className="w-full p-3 border rounded pr-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                aria-label={
                  showConfirmPassword
                    ? t("forgotPassword").hidePassword
                    : t("forgotPassword").showPassword
                }
              >
                {showConfirmPassword ? (
                  <AiFillEyeInvisible size={20} />
                ) : (
                  <Eye size={20} />
                )}
              </button>
            </div>

            <button
              onClick={handleResetPassword}
              className="w-full bg-green-600 text-white py-3 rounded hover:bg-green-700 transition"
              disabled={loading}
            >
              {loading
                ? t("forgotPassword").resetting
                : t("forgotPassword").resetPassword}
            </button>
          </>
        )}

        <button
          onClick={handleBack}
          className="mt-5 text-green-600 hover:underline text-sm"
        >
          {t("forgotPassword").back}
        </button>
      </div>
    </div>
  );
};

export default ForgotPassword;
